import { useConsumerFinancialSummary, usePlanData, useServiceCharges } from "@features/bx/singleBillGenaration/hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import {
  AlertCircle,
  Clock,
  CreditCard,
  DollarSign,
  Info,
  Wallet,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";

type CategorizedServiceCharges = Record<string, any[]>;

const CATEGORY_CONFIG: Record<string, { title: string, desc: string, colorClass: string, bgColorClass: string, dotClass: string, headerClass: string }> = {
  fixedCharges: {
    title: "Fixed Charges",
    desc: "Recurring flat fees applied every billing cycle regardless of consumption.",
    colorClass: "text-blue-700",
    bgColorClass: "bg-blue-50/50",
    dotClass: "bg-blue-500",
    headerClass: "bg-blue-50/30",
  },
  surcharges: {
    title: "Surcharges",
    desc: "Additional adjustments applied on top of base charges to recover variable costs.",
    colorClass: "text-amber-700",
    bgColorClass: "bg-amber-50/50",
    dotClass: "bg-amber-500",
    headerClass: "bg-amber-50/30",
  },
  taxes: {
    title: "Taxes",
    desc: "Government and local taxes applied to the bill.",
    colorClass: "text-purple-700",
    bgColorClass: "bg-purple-50/50",
    dotClass: "bg-purple-500",
    headerClass: "bg-purple-50/30",
  },
  penalties: {
    title: "Penalties",
    desc: "Charges applied for late payments, policy violations, or service incidents.",
    colorClass: "text-red-700",
    bgColorClass: "bg-red-50/50",
    dotClass: "bg-red-500",
    headerClass: "bg-red-50/30",
  }
};

interface PaymentToggles {
  outstanding: boolean;
  late_fees: boolean;
  credit_balance: boolean;
  installments: boolean;
  serviceCharges: Record<string, string[]>;
}

interface PaymentsServiceChargesProps {
  planId: string;
  consumerNo: string;
  paymentsUpdates: any;
  onPaymentSettingsUpdate?: (paymentSettings: any, serviceCharges: any) => void;
}

function PaymentsServiceCharges({
  planId,
  consumerNo,
  paymentsUpdates,
  onPaymentSettingsUpdate,
}: PaymentsServiceChargesProps) {
  const CURRENCY = useCurrency();
  
  // Ref to track if state has been initialized
  const isInitializedRef = useRef(false);
  const { data: financeSummary } = useConsumerFinancialSummary(consumerNo);
  
  // Single state to manage all toggles - initialize from props
  const [toggles, setToggles] = useState<PaymentToggles>(() => ({
    outstanding: paymentsUpdates?.outstanding ?? true,
    late_fees: paymentsUpdates?.late_fees ?? true,
    credit_balance: paymentsUpdates?.credit_balance ?? true,
    installments: paymentsUpdates?.installments ?? true,
    serviceCharges: paymentsUpdates?.serviceCharges ?? {},
  }));

  const { data: serviceChargesData } = usePlanData(planId);
  const serviceCharges = serviceChargesData?.serviceChargesForBilling as CategorizedServiceCharges | undefined;

  /* -------------------- Initialize Service Charges -------------------- */

  useEffect(() => {
    if (!serviceCharges || isInitializedRef.current) return;

    // Check if we have any service charges from parent state
    const hasParentServiceCharges = 
      paymentsUpdates?.serviceCharges && 
      Object.keys(paymentsUpdates.serviceCharges).length > 0 &&
      Object.values(paymentsUpdates.serviceCharges).some(val => Array.isArray(val));

    if (hasParentServiceCharges) {
      // Use parent state if available
      setToggles((prev) => ({
        ...prev,
        serviceCharges: paymentsUpdates.serviceCharges,
      }));
      isInitializedRef.current = true;
    } else {
      // Initialize all service charges to true
      const initialServiceCharges: Record<string, string[]> = {};
      Object.keys(CATEGORY_CONFIG).forEach(catKey => {
        const arr = serviceCharges[catKey];
        const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
        if (Array.isArray(arr)) {
          initialServiceCharges[snakeKey] = arr.map((c: any) => c.code || c.name);
        }
      });

      setToggles((prev) => ({
        ...prev,
        serviceCharges: initialServiceCharges,
      }));
      isInitializedRef.current = true;
    }
  }, [serviceCharges, paymentsUpdates?.serviceCharges]);

  /* -------------------- Send data to parent -------------------- */

  useEffect(() => {
    // Don't sync until after initialization
    if (!onPaymentSettingsUpdate || !isInitializedRef.current) return;

    const paymentSettings = {
      outstanding: toggles.outstanding,
      late_fees: toggles.late_fees,
      credit_balance: toggles.credit_balance,
      installments: toggles.installments,
    };

    const serviceChargesPayload = toggles.serviceCharges;

    onPaymentSettingsUpdate(paymentSettings, serviceChargesPayload);
  }, [toggles, onPaymentSettingsUpdate]);

  /* -------------------- Handlers -------------------- */

  const handlePaymentToggle = useCallback(
    (field: keyof Omit<PaymentToggles, "serviceCharges">, checked: boolean) => {
      setToggles((prev) => ({
        ...prev,
        [field]: checked,
      }));
    },
    []
  );


  const { totalCharges, includedCount } = useMemo(() => {
    if (!serviceCharges) return { totalCharges: 0, includedCount: 0 };
    let total = 0;
    let included = 0;
    
    Object.keys(CATEGORY_CONFIG).forEach((key) => {
      const arr = serviceCharges[key];
      const snakeKey = key === "fixedCharges" ? "fixed_charges" : key;
      if (Array.isArray(arr)) {
        total += arr.length;
        included += (toggles.serviceCharges[snakeKey] || []).length;
      }
    });

    return { totalCharges: total, includedCount: included };
  }, [serviceCharges, toggles.serviceCharges]);

  /* -------------------- Render -------------------- */

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Outstanding Card */}
        <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 transition-all duration-200 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Outstandings
                </h3>
                <p className="text-xs text-gray-500 line-clamp-1">
                  {financeSummary?.outstanding?.description || "Carry forward balances"}
                </p>
              </div>
            </div>
            <Switch
              checked={toggles.outstanding}
              onCheckedChange={(checked) =>
                handlePaymentToggle("outstanding", checked)
              }
            />
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                Amount
              </div>
              <div className="text-lg font-bold text-gray-900">
                {CURRENCY} {financeSummary?.outstanding?.amount?.toFixed(2) || "0.00"}
              </div>
            </div>

            {!toggles.outstanding && (
              <div className="mt-2 pt-2 border-t border-blue-200/50 flex items-center gap-1.5 text-amber-700 text-[10px] font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="h-3 w-3" />
                Not included in bill
              </div>
            )}
          </div>
        </div>

        {/* Late Fees Card */}
        <div className="bg-orange-50/50 border border-orange-100 rounded-xl p-4 transition-all duration-200 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Late Fees</h3>
                <p className="text-xs text-gray-500 line-clamp-1">
                  {financeSummary?.lateFees?.description || "Apply applicable fees"}
                </p>
              </div>
            </div>
            <Switch
              checked={toggles.late_fees}
              onCheckedChange={(checked) =>
                handlePaymentToggle("late_fees", checked)
              }
            />
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                Amount
              </div>
              <div className="text-lg font-bold text-gray-900">
                {CURRENCY} {financeSummary?.lateFees?.amount?.toFixed(2) || "0.00"}
              </div>
            </div>

            {!toggles.late_fees && (
              <div className="mt-2 pt-2 border-t border-orange-200/50 flex items-center gap-1.5 text-amber-700 text-[10px] font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="h-3 w-3" />
                Not applied to bill
              </div>
            )}
          </div>
        </div>

        {/* Credit Balance Card */}
        <div className="bg-green-50/50 border border-green-100 rounded-xl p-4 transition-all duration-200 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Credit Balance
                </h3>
                <p className="text-xs text-gray-500 line-clamp-1">
                  {financeSummary?.creditBalance?.description || "Adjust using credit"}
                </p>
              </div>
            </div>
            <Switch
              checked={toggles.credit_balance}
              onCheckedChange={(checked) =>
                handlePaymentToggle("credit_balance", checked)
              }
            />
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                Available
              </div>
              <div className="text-lg font-bold text-gray-900">
                {CURRENCY} {financeSummary?.creditBalance?.amount?.toFixed(2) || "0.00"}
              </div>
            </div>

            {!toggles.credit_balance && (
              <div className="mt-2 pt-2 border-t border-green-200/50 flex items-center gap-1.5 text-emerald-700 text-[10px] font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="h-3 w-3" />
                Not adjusted in bill
              </div>
            )}
          </div>
        </div>

        {/* Payment Installment Card */}
        <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-4 transition-all duration-200 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Installments
                </h3>
                <p className="text-xs text-gray-500 line-clamp-1">
                  {financeSummary?.installments?.description || "Active plan payments"}
                </p>
              </div>
            </div>
            <Switch
              checked={toggles.installments}
              onCheckedChange={(checked) =>
                handlePaymentToggle("installments", checked)
              }
            />
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                Amount
              </div>
              <div className="text-lg font-bold text-gray-900">
                {CURRENCY} {financeSummary?.installments?.amount?.toFixed(2) || "0.00"}
              </div>
            </div>

            {!toggles.installments && (
              <div className="mt-2 pt-2 border-t border-purple-200/50 flex items-center gap-1.5 text-purple-700 text-[10px] font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="h-3 w-3" />
                Not included in bill
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Service Charges Inclusion</h3>
            <p className="text-sm text-gray-500">Toggle individual charges or entire categories to include or exclude from this billing cycle.</p>
          </div>
          <div className="text-sm font-medium text-gray-500">
            <span className="text-blue-600 font-bold">{includedCount}</span> / {totalCharges} charges included
          </div>
        </div>

        {["fixedCharges", "surcharges", "taxes", "penalties"].map(catKey => {
          const charges = serviceCharges?.[catKey] || [];
          if (!charges.length) return null;

          const config = CATEGORY_CONFIG[catKey];
          const snakeKey = catKey === "fixedCharges" ? "fixed_charges" : catKey;
          const catIncludedCount = (toggles.serviceCharges[snakeKey] || []).length;
          const catTotalCount = charges.length;
          const isAllIncluded = catTotalCount > 0 && catIncludedCount === catTotalCount;

          const handleToggleCategory = (checked: boolean) => {
            setToggles(prev => {
              const newArr = checked ? charges.map((c: any) => c.code || c.name) : [];
              return { ...prev, serviceCharges: { ...prev.serviceCharges, [snakeKey]: newArr } };
            });
          };

          return (
            <div key={catKey} className="border rounded-xl overflow-hidden bg-white shadow-sm">
              {/* Category Header */}
              <div className={`p-4 flex items-center justify-between border-b ${config.headerClass}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-3 h-3 mt-1.5 rounded-full ${config.dotClass}`}></div>
                  <div>
                    <h4 className={`font-bold text-base ${config.colorClass}`}>{config.title}</h4>
                    <p className="text-sm text-gray-600">{config.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <span className={`text-sm font-semibold ${config.colorClass}`}>
                    {catIncludedCount} of {catTotalCount} included
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 font-medium">Include all</span>
                    <Switch 
                      checked={isAllIncluded} 
                      onCheckedChange={handleToggleCategory} 
                    />
                  </div>
                </div>
              </div>

              {/* List of charges */}
              <div className="bg-white">
                {charges.map((charge: any, idx: number) => {
                  const id = charge.code || charge.name;
                  const isIncluded = (toggles.serviceCharges[snakeKey] || []).includes(id);

                  return (
                    <div key={id} className="p-4 flex items-center justify-between border-b last:border-b-0 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-6">
                        <span className="text-gray-400 font-mono text-sm w-4 text-center">{idx + 1}</span>
                        <div>
                          <h5 className="font-semibold text-gray-900">{charge.name}</h5>
                        </div>
                      </div>
                      <div className="flex items-center gap-8">
                        {charge.price && <span className="font-semibold text-gray-900">{CURRENCY} {charge.price}</span>}
                        <div className="flex items-center gap-3 w-[120px] justify-end">
                          <Badge 
                            variant={isIncluded ? "success" : "secondary"} 
                          >
                            {isIncluded ? "Included" : "Excluded"}
                          </Badge>
                          <Switch 
                            checked={isIncluded} 
                            onCheckedChange={(checked) => {
                              setToggles((prev) => {
                                const currentArr = prev.serviceCharges[snakeKey] || [];
                                const newArr = checked ? [...currentArr, id] : currentArr.filter(v => v !== id);
                                return {
                                  ...prev,
                                  serviceCharges: {
                                    ...prev.serviceCharges,
                                    [snakeKey]: newArr,
                                  },
                                };
                              });
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default PaymentsServiceCharges;